/**
 * Import Help Component
 * 
 * Provides comprehensive instructions and CSV format examples
 * Helps users understand the import process and requirements
 */

import React, { useState } from 'react';
import { HelpCircle, Download, X, FileText, CheckCircle, AlertCircle } from 'lucide-react';

const ImportHelp = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const downloadSampleCSV = () => {
    const sampleData = [
      'Application / system name,Description,Location,Asset Owner Department,Asset Owner Name,E-Mail,Security Classification,Criticality Classification,Customer PII,Employee PII,Board Information,Executive Information,Financial Information,SSO,RBAC,2FA,Network Segmentation,Data Encryption,User Access Reviews,Advanced Endpoint Protection,Onboarded onto SOC,Backups',
      'Customer Portal,Main customer-facing web application,AWS US-East-1,IT Operations,John Smith,john.smith@company.com,Confidential,2-Very High,YES,NO,NO,NO,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES',
      'HR Management System,Employee records and payroll system,On-premises Data Center,Human Resources,Jane Doe,jane.doe@company.com,Highly Confidential,1-Extremely,NO,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES',
      'Marketing Database,Customer analytics and campaign management,Azure West-2,Marketing,Bob Johnson,bob.johnson@company.com,Internal Use,3-High,YES,NO,NO,NO,NO,NO,YES,NO,YES,NO,YES,NO,YES,YES',
      'Financial Reporting,Monthly and quarterly financial reports,On-premises Data Center,Finance,Alice Brown,alice.brown@company.com,Highly Confidential,2-Very High,NO,NO,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES,YES'
    ];
    
    const csvContent = sampleData.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crown-jewels-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: HelpCircle },
    { id: 'format', label: 'File Format', icon: FileText },
    { id: 'fields', label: 'Field Guide', icon: CheckCircle },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: AlertCircle }
  ];

  const requiredFields = [
    { name: 'Application / system name', description: 'Name of the application or system', example: 'Customer Portal' },
    { name: 'Asset Owner Department', description: 'Department responsible for the asset', example: 'IT Operations' },
    { name: 'Asset Owner Name', description: 'Name of the asset owner', example: 'John Smith' },
    { name: 'E-Mail', description: 'Email address of the asset owner', example: 'john.smith@company.com' }
  ];

  const optionalFields = [
    { name: 'Description', description: 'Brief description of the asset', example: 'Main customer-facing web application' },
    { name: 'Location', description: 'Physical or logical location', example: 'AWS US-East-1' },
    { name: 'Security Classification', description: 'Internal Use, Confidential, or Highly Confidential', example: 'Confidential' },
    { name: 'Criticality Classification', description: '1-Extremely, 2-Very High, 3-High, 4-Medium, or 5-Low', example: '2-Very High' },
    { name: 'Customer PII', description: 'Contains customer personal information (YES/NO)', example: 'YES' },
    { name: 'Employee PII', description: 'Contains employee personal information (YES/NO)', example: 'NO' },
    { name: 'Board Information', description: 'Contains board-level information (YES/NO)', example: 'NO' },
    { name: 'Executive Information', description: 'Contains executive information (YES/NO)', example: 'NO' },
    { name: 'Financial Information', description: 'Contains financial information (YES/NO)', example: 'YES' },
    { name: 'SSO', description: 'Single Sign-On implemented (YES/NO/N/A)', example: 'YES' },
    { name: 'RBAC', description: 'Role-Based Access Control implemented (YES/NO/N/A)', example: 'YES' },
    { name: '2FA', description: 'Two-Factor Authentication implemented (YES/NO/N/A)', example: 'YES' },
    { name: 'Network Segmentation', description: 'Network segmentation implemented (YES/NO/N/A)', example: 'YES' },
    { name: 'Data Encryption', description: 'Data encryption implemented (YES/NO/N/A)', example: 'YES' },
    { name: 'User Access Reviews', description: 'Regular access reviews conducted (YES/NO/N/A)', example: 'YES' },
    { name: 'Advanced Endpoint Protection', description: 'Advanced endpoint protection deployed (YES/NO/N/A)', example: 'YES' },
    { name: 'Onboarded onto SOC', description: 'Monitored by Security Operations Center (YES/NO/N/A)', example: 'YES' },
    { name: 'Backups', description: 'Regular backups implemented (YES/NO/N/A)', example: 'YES' }
  ];

  const commonIssues = [
    {
      issue: 'Column headers not recognized',
      solution: 'Ensure column headers match exactly as shown in the field guide. Headers are case-sensitive.',
      example: 'Use "Application / system name" not "Application Name"'
    },
    {
      issue: 'Invalid email addresses',
      solution: 'Ensure all email addresses are properly formatted with @ symbol and domain.',
      example: 'user@company.com is valid, user@company is not'
    },
    {
      issue: 'Invalid dropdown values',
      solution: 'Use only the specified values for dropdown fields like Security Classification.',
      example: 'Use "Confidential" not "CONFIDENTIAL" or "confidential"'
    },
    {
      issue: 'File too large',
      solution: 'Split large files into smaller chunks of 1000 rows or less, or reduce file size to under 10MB.',
      example: 'Split a 2000-row file into two 1000-row files'
    },
    {
      issue: 'Special characters in data',
      solution: 'Ensure CSV is properly encoded and special characters are handled correctly.',
      example: 'Use UTF-8 encoding when saving CSV files'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Crown Jewels Import Help</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Import Process Overview</h3>
                <p className="text-gray-700 mb-4">
                  The Crown Jewels import feature allows you to bulk import asset inventory data from CSV or Excel files. 
                  Follow these steps for a successful import:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>Prepare your CSV/Excel file with the required columns</li>
                  <li>Select the target client for the import</li>
                  <li>Upload your file (max 10MB)</li>
                  <li>Review the data preview and column mapping</li>
                  <li>Fix any validation errors</li>
                  <li>Execute the import</li>
                  <li>Review the import summary</li>
                </ol>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">💡 Pro Tips</h4>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Start with a small test file (5-10 rows) to verify your format</li>
                  <li>• Use the provided sample CSV as a template</li>
                  <li>• Keep a backup of your original file</li>
                  <li>• Import data during low-usage hours for large files</li>
                  <li>• You can rollback imports within 5 minutes if needed</li>
                </ul>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={downloadSampleCSV}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Sample CSV</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'format' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">File Format Requirements</h3>
                <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Supported Formats</h4>
                    <p className="text-sm text-gray-600">CSV (.csv), Excel (.xlsx, .xls)</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">File Size Limit</h4>
                    <p className="text-sm text-gray-600">Maximum 10MB per file</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Encoding</h4>
                    <p className="text-sm text-gray-600">UTF-8 recommended for special characters</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Structure</h4>
                    <p className="text-sm text-gray-600">First row must contain column headers, data starts from row 2</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">CSV Format Example</h4>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="whitespace-nowrap">
                    Application / system name,Asset Owner Department,Asset Owner Name,E-Mail,...
                  </div>
                  <div className="whitespace-nowrap">
                    Customer Portal,IT Operations,John Smith,john.smith@company.com,...
                  </div>
                  <div className="whitespace-nowrap">
                    HR System,Human Resources,Jane Doe,jane.doe@company.com,...
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">⚠️ Important Notes</h4>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Column headers must match exactly (case-sensitive)</li>
                  <li>• Empty rows will be automatically skipped</li>
                  <li>• Commas in data values should be quoted: "Value, with comma"</li>
                  <li>• Date values should be in standard formats (YYYY-MM-DD)</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Required Fields</h3>
                <div className="space-y-3">
                  {requiredFields.map((field, index) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{field.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                          <p className="text-xs text-blue-600 mt-1">Example: {field.example}</p>
                        </div>
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Required</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Optional Fields</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {optionalFields.map((field, index) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{field.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                          <p className="text-xs text-blue-600 mt-1">Example: {field.example}</p>
                        </div>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Optional</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'troubleshooting' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Common Issues & Solutions</h3>
                <div className="space-y-4">
                  {commonIssues.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <h4 className="font-medium text-red-800 flex items-center space-x-2">
                        <AlertCircle className="h-4 w-4" />
                        <span>{item.issue}</span>
                      </h4>
                      <p className="text-gray-700 mt-2">{item.solution}</p>
                      <p className="text-sm text-blue-600 mt-1 font-mono">{item.example}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">✅ Best Practices</h4>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• Test with a small file first (5-10 rows)</li>
                  <li>• Validate your data in Excel before exporting to CSV</li>
                  <li>• Use consistent formatting throughout your file</li>
                  <li>• Keep field values concise and clear</li>
                  <li>• Review the validation report carefully before importing</li>
                  <li>• Use the rollback feature if you need to undo an import</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Need more help? Check the validation messages during import for specific guidance.
            </div>
            <div className="flex space-x-3">
              <button
                onClick={downloadSampleCSV}
                className="flex items-center space-x-2 px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                <Download className="h-4 w-4" />
                <span>Sample CSV</span>
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportHelp; 